import type { AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * The prefill indicator (Figma "Autofilled" pattern, "Scan batch ticket" 32261:38786): an "Autofilled" badge beside
 * each field the linked map feature filled, and a summary card "<n> fields autofilled / From <layer> · make sure to
 * review them" at the top of the form header. Session state only (never saved).
 */
export const AUTOFILLED_BADGE = 'Autofilled';

export function autofillSummary(count: number, layerName: string | null | undefined): { title: string; subtitle: string } {
  const name = typeof layerName === 'string' ? layerName.trim() : '';
  return {
    title: `${count} ${count === 1 ? 'field' : 'fields'} autofilled`,
    subtitle: name ? `From ${name} · make sure to review them` : 'Make sure to review them',
  };
}

/**
 * Which fields are still autofilled, keyed by field name. Call `track` right after the prefill patch: the patch's own
 * emissions are over by then, so the next value change of a field is the user's edit, which clears that field.
 */
export class PrefillCaptionTracker {
  private readonly sources = new Map<string, string>();
  private readonly watchers = new Map<string, Subscription>();

  /** `onChange` runs when an edit clears a field (e.g. to run change detection). */
  constructor(private readonly onChange?: () => void) {}

  isAutofilled(name: string): boolean {
    return this.sources.has(name);
  }

  /** Fields still autofilled. */
  count(): number {
    return this.sources.size;
  }

  /** The source (layer name) of the latest prefill still shown, '' when none. */
  source(): string {
    return [...this.sources.values()].pop() ?? '';
  }

  track(controls: Record<string, AbstractControl | null | undefined>, source: string): void {
    for (const [name, control] of Object.entries(controls)) {
      this.watchers.get(name)?.unsubscribe();
      this.watchers.delete(name);
      this.sources.delete(name);
      if (!control) continue;
      this.sources.set(name, source || '');
      this.watchers.set(name, control.valueChanges.pipe(take(1)).subscribe(() => {
        this.sources.delete(name);
        this.watchers.delete(name);
        this.onChange?.();
      }));
    }
  }

  /** Clears everything (another form type, or the editor goes away). */
  clear(): void {
    this.watchers.forEach(watcher => watcher.unsubscribe());
    this.watchers.clear();
    this.sources.clear();
  }
}
